import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import api, { API_BASE } from '../services/api';
import { ModalEmail } from '../components/PrintView';
import SoportesFactura from '../components/SoportesFactura';
import { hoyColombia } from '../utils/fecha';

// Iconos SVG del historial (reemplazan los emojis, más compactos).
const HIST_ICONS = {
  creacion: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></>,
  email:    <><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m2 7 10 7 10-7"/></>,
  pago:     <><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></>,
  estado:   <><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></>,
  nota:     <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
  radian:   <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></>,
  dian:     <><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 21v-6h6v6"/></>,
};

const ESTADOS = ['borrador','aprobada','pagada','vencida','anulada'];
const EC = { borrador:'#94a3b8', aprobada:'#3b82f6', pagada:'#10b981', vencida:'#ef4444', anulada:'#475569' };
const fmt  = (n) => '$' + Number(n || 0).toLocaleString('es-CO');
const pct  = (n) => Number(n || 0).toFixed(2) + '%';
const fmtF = (d) => d ? new Date(d).toLocaleDateString('es-CO') : '—';

const DIAN_QR_BASE = 'https://catalogo-vpfe.dian.gov.co/document/searchqr?documentkey=';

const ITEM0 = () => ({ descripcion:'', cantidad:1, unidad:'und', precio_unitario:'', descuento_pct:0, iva_pct:19 });

const EVENTOS_RADIAN = {
  '030': { label:'Acuse de recibo',          icon:'📬', desc:'Confirma que se recibió la factura electrónica', color:'#1e3a5f', badge:'#3b82f6' },
  '031': { label:'Reclamo',                  icon:'⚠️',  desc:'Reclamo sobre la factura (datos, mercancía, servicio)', color:'#78350f', badge:'#f59e0b' },
  '032': { label:'Recibo del bien/servicio', icon:'✅',  desc:'Confirma que se recibió la mercancía o el servicio', color:'#14532d', badge:'#22c55e' },
  '033': { label:'Aceptación expresa',       icon:'🤝',  desc:'El adquiriente acepta expresamente la factura', color:'#14532d', badge:'#10b981' },
  '034': { label:'Aceptación tácita',        icon:'🕐',  desc:'Transcurrieron 3 días hábiles sin reclamación', color:'#312e81', badge:'#818cf8' },
};
const MOTIVOS_031 = {
  '01':'Datos de la factura no corresponden',
  '02':'Mercancía no entregada',
  '03':'Servicio no prestado',
  '04':'Otro',
};

// DIAN Anexo Técnico 1.9 — Códigos de Nota Crédito (tipodoc 91)
const TIPOS_NC = [
  { val:'1', label:'1 — Devolución parcial de bienes/servicios', desc:'Use cuando el cliente devuelve parte de la mercancía o servicio prestado.' },
  { val:'2', label:'2 — Anulación total de la factura',          desc:'Anula completamente la factura original. Se registrará automáticamente como anulada.' },
  { val:'3', label:'3 — Descuento total sobre factura',          desc:'Concede un descuento global sobre el valor de la factura original.' },
  { val:'4', label:'4 — Ajuste de precio',                       desc:'Corrección del precio pactado, inferior al facturado.' },
  { val:'5', label:'5 — Otra',                                   desc:'Otros motivos de corrección no contemplados en los anteriores.' },
];
// DIAN Anexo Técnico 1.9 — Códigos de Nota Débito (tipodoc 92)
const TIPOS_ND = [
  { val:'1', label:'1 — Intereses',                 desc:'Cargo por intereses de mora u otro tipo de interés pactado.' },
  { val:'2', label:'2 — Gastos por cobrar',         desc:'Gastos adicionales que deben cargarse al adquiriente (fletes, seguros, etc.).' },
  { val:'3', label:'3 — Cambio de valor / precio',  desc:'Incremento en el precio o valor de los bienes/servicios.' },
  { val:'4', label:'4 — Otro',                      desc:'Otros conceptos de ajuste en aumento no contemplados anteriormente.' },
];
// DIAN Art 616-1 ET — Tipos de Documento Equivalente
const TIPOS_DE = [
  { val:'01', label:'01 — Boleta / entrada a espectáculos públicos' },
  { val:'02', label:'02 — Recibo de caja' },
  { val:'04', label:'04 — Tiquete de máquina registradora POS' },
  { val:'05', label:'05 — Tiquete de transporte' },
  { val:'07', label:'07 — Recibo de pago de arrendamiento' },
  { val:'08', label:'08 — Otro documento equivalente' },
];

// Animación spinner inyectada una sola vez
if (!document.getElementById('df-spin-style')) {
  const st = document.createElement('style');
  st.id = 'df-spin-style';
  st.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(st);
}

export default function DetalleFactura() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [fac,       setFac]       = useState(null);
  const [notas,     setNotas]     = useState([]);
  const [loading,   setLoad]      = useState(true);
  const [enviandoDian, setEnviandoDian] = useState(false);
  const [qrImg, setQrImg] = useState(null);
  const [email,        setEmail]       = useState(false);
  const [modalPago,    setModalPago]   = useState(false);
  const [radianAbierto,setRadianAbierto] = useState(null); // código del evento expandido
  const [modalNota, setModalNota] = useState(null); // 'debito' | 'credito'
  const [saving,       setSaving]      = useState(false);
  const [formPago,     setFormPago]    = useState({ monto:'', fecha_pago:'', medio_pago:'transferencia', referencia:'', notas:'' });
  const [formNota,     setFormNota]    = useState({ concepto_nota:'', tipo_nota:'1', notas:'', items:[ITEM0()], periodo_inicio:'', periodo_fin:'' });
  const [modalDE,      setModalDE]     = useState(false);
  const [formDE,       setFormDE]      = useState({ tipo_de:'04', concepto:'', notas:'', medio_pago_desc:'Efectivo', items:[ITEM0()] });
  const [eventos,      setEventos]     = useState([]);
  const [historial,    setHistorial]   = useState([]);
  const [pdfUrl,       setPdfUrl]      = useState(null);
  const [pdfLoading,   setPdfLoading]  = useState(true);
  const pdfBlobRef = useRef(null);
  const soportesRef = useRef(null);
  const [modalRadian,  setModalRadian] = useState(false);
  const [savingRadian, setSavingRadian]= useState(false);
  const [formRadian,   setFormRadian]  = useState({ codigo:'030', motivo_codigo:'02', motivo_desc:'', responsable:{ id:'', nombre:'', apellido:'', cargo:'', area:'' } });

  const descargarArchivo = (url, filename) => {
    const token = localStorage.getItem('erp_token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => alert('Error al descargar el archivo'));
  };

  const loadEventos = () =>
    api.get(`/radian/${id}/eventos`).then(r => setEventos(r.data)).catch(() => {});

  const loadHistorial = () =>
    api.get(`/facturacion/${id}/historial`).then(r => setHistorial(r.data)).catch(() => {});

  const loadPDF = () => {
    setPdfLoading(true);
    const token = localStorage.getItem('erp_token');
    fetch(`${API_BASE}/api/facturacion/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        if (pdfBlobRef.current) URL.revokeObjectURL(pdfBlobRef.current);
        const url = URL.createObjectURL(blob);
        pdfBlobRef.current = url;
        setPdfUrl(url);
      })
      .catch(() => setPdfUrl(null))
      .finally(() => setPdfLoading(false));
  };

  useEffect(() => {
    return () => { if (pdfBlobRef.current) URL.revokeObjectURL(pdfBlobRef.current); };
  }, []);

  const load = () =>
    Promise.all([
      api.get(`/facturacion/${id}`),
      api.get(`/facturacion/${id}/notas`),
    ])
    .then(([r, rn]) => {
      setFac(r.data);
      setNotas(rn.data);
      loadEventos();
      loadHistorial();
      loadPDF();
    })
    .catch(() => navigate('/facturacion'))
    .finally(() => setLoad(false));

  useEffect(() => { load(); }, [id]);

  // QR DIAN en pantalla — solo cuando la factura fue ACEPTADA (tiene CUFE válido)
  useEffect(() => {
    const aceptada = fac?.cufe && (fac?.estado_fe === 'aceptada' || ['0','00'].includes(String(fac?.dian_status_code || '')));
    if (aceptada) {
      QRCode.toDataURL(`${DIAN_QR_BASE}${fac.cufe}`, { width: 130, margin: 1 })
        .then(setQrImg).catch(() => setQrImg(null));
    } else {
      setQrImg(null);
    }
  }, [fac?.estado_fe, fac?.cufe, fac?.dian_status_code]);

  const cambiarEstado = async (estado) => {
    await api.patch(`/facturacion/${id}/estado`, { estado });
    load();
  };

  const handleEnviarDIAN = async () => {
    if (!window.confirm('¿Enviar esta factura a la DIAN? Se firmará y transmitirá para su validación electrónica. Si la DIAN la acepta, quedará aprobada con su CUFE y QR.')) return;
    setEnviandoDian(true);
    try {
      const { data } = await api.post(`/facturacion/${id}/enviar-dian`);
      if (data.warning) {
        alert(`✅ Factura ACEPTADA por la DIAN\nCUFE: ${(data.cufe || '').slice(0, 30)}…\n\n⚠️ ${data.warning}`);
      } else {
        alert(`✅ Factura ACEPTADA por la DIAN 🎉\nCUFE: ${data.cufe || ''}`);
      }
      load();
    } catch (e) {
      const d = e?.response?.data;
      const detalle = (d?.errorMessages || d?.errores || d?.detalles || []);
      alert('❌ ' + (d?.error || 'Error al enviar a la DIAN') + (detalle.length ? '\n\n' + detalle.join('\n') : ''));
    } finally { setEnviandoDian(false); }
  };

  const handlePago = async () => {
    if (!formPago.monto) return alert('Ingrese el monto');
    setSaving(true);
    try {
      await api.post(`/facturacion/${id}/pagos`, formPago);
      setModalPago(false);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const addItemNota    = () => setFormNota(f => ({ ...f, items:[...f.items, ITEM0()] }));
  const removeItemNota = i  => setFormNota(f => ({ ...f, items:f.items.filter((_,idx)=>idx!==i) }));
  const updItemNota    = (i,k,v) => setFormNota(f => ({ ...f, items:f.items.map((it,idx)=>idx===i?{...it,[k]:v}:it) }));
  const addItemDE      = () => setFormDE(f => ({ ...f, items:[...f.items, { ...ITEM0(), iva_pct:0 }] }));
  const removeItemDE   = i  => setFormDE(f => ({ ...f, items:f.items.filter((_,idx)=>idx!==i) }));
  const updItemDE      = (i,k,v) => setFormDE(f => ({ ...f, items:f.items.map((it,idx)=>idx===i?{...it,[k]:v}:it) }));

  const calcNotaTotales = () => {
    let sub=0, iv=0;
    formNota.items.forEach(it => {
      const base = parseFloat(it.cantidad||0)*parseFloat(it.precio_unitario||0)*(1-(parseFloat(it.descuento_pct||0)/100));
      const ivaV = base*(parseFloat(it.iva_pct??19)/100);
      sub+=base; iv+=ivaV;
    });
    return { sub, iv, total:sub+iv };
  };

  const calcDETotales = () => {
    let sub=0, iv=0;
    formDE.items.forEach(it => {
      const base = parseFloat(it.cantidad||0)*parseFloat(it.precio_unitario||0)*(1-(parseFloat(it.descuento_pct||0)/100));
      const ivaV = base*(parseFloat(it.iva_pct??0)/100);
      sub+=base; iv+=ivaV;
    });
    return { sub, iv, total:sub+iv };
  };

  // Carga los ítems de la factura original al formulario de nota
  // Usa fac.items ya cargados — no hace petición adicional
  const cargarItemsOriginal = (esAnulacion = false) => {
    const origen = fac?.items || [];
    if (!origen.length) {
      alert('No se encontraron ítems en la factura original');
      return;
    }
    const itemsOri = origen.map(it => ({
      descripcion:     it.descripcion || '',
      cantidad:        esAnulacion ? Number(it.cantidad) : 1,
      unidad:          it.unidad || 'und',
      precio_unitario: String(it.precio_unitario || ''),
      descuento_pct:   Number(it.descuento_pct || 0),
      iva_pct:         Number(it.iva_pct ?? 19),
    }));
    setFormNota(f => ({ ...f, items: itemsOri }));
  };

  const handleCrearNota = async () => {
    if (!formNota.concepto_nota) return alert('Ingrese el concepto de la nota');
    if (formNota.items.some(it=>!it.descripcion||!it.precio_unitario)) return alert('Complete todos los ítems');
    setSaving(true);
    try {
      const endpoint = modalNota === 'debito'
        ? `/facturacion/${id}/nota-debito`
        : `/facturacion/${id}/nota-credito`;
      // Agregar período al campo notas si se especificó
      const notasConPeriodo = [
        formNota.notas,
        (formNota.periodo_inicio && formNota.periodo_fin)
          ? `Período: ${formNota.periodo_inicio} al ${formNota.periodo_fin}`
          : '',
      ].filter(Boolean).join(' | ');
      const payload = { ...formNota, notas: notasConPeriodo || undefined };
      const { data: nueva } = await api.post(endpoint, payload);
      setModalNota(null);
      setFormNota({ concepto_nota:'', tipo_nota:'1', notas:'', items:[ITEM0()], periodo_inicio:'', periodo_fin:'' });
      alert(`✅ ${modalNota === 'debito' ? 'Nota Débito' : 'Nota Crédito'} creada: ${nueva.numero}`);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const handleCrearDE = async () => {
    if (!formDE.concepto) return alert('Ingrese el concepto del documento');
    if (formDE.items.some(it=>!it.descripcion||!it.precio_unitario)) return alert('Complete todos los ítems');
    setSaving(true);
    try {
      const { data: nuevo } = await api.post(`/facturacion/documento-equivalente`, {
        ...formDE,
        cliente_id: fac.cliente_id,
      });
      setModalDE(false);
      setFormDE({ tipo_de:'04', concepto:'', notas:'', medio_pago_desc:'Efectivo', items:[ITEM0()] });
      alert(`✅ Documento Equivalente creado: ${nuevo.numero}`);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Error al crear documento equivalente'); }
    finally { setSaving(false); }
  };

  const handleCrearEventoRadian = async () => {
    setSavingRadian(true);
    try {
      const body = {
        codigo:        formRadian.codigo,
        motivo_codigo: formRadian.codigo === '031' ? formRadian.motivo_codigo : undefined,
        motivo_desc:   formRadian.codigo === '031' ? formRadian.motivo_desc   : undefined,
        responsable:   (formRadian.codigo === '030' || formRadian.codigo === '032')
                         ? formRadian.responsable
                         : undefined,
      };
      await api.post(`/radian/${id}/eventos`, body);
      setModalRadian(false);
      setFormRadian({ codigo:'030', motivo_codigo:'02', motivo_desc:'', responsable:{ id:'', nombre:'', apellido:'', cargo:'', area:'' } });
      loadEventos();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al registrar evento RADIAN');
    } finally {
      setSavingRadian(false);
    }
  };

  const eliminarEvento = async (evId) => {
    if (!window.confirm('¿Eliminar este evento RADIAN?')) return;
    try {
      await api.delete(`/radian/${id}/eventos/${evId}`);
      loadEventos();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const descargarEventoXML = (evId, cod) => {
    const token = localStorage.getItem('erp_token');
    fetch(`${API_BASE}/api/radian/${id}/eventos/${evId}/xml`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `RADIAN_${cod}_${evId}.xml`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => alert('Error al descargar XML del evento'));
  };

  if (loading) return <div style={{ color:'#64748b', textAlign:'center', padding:60 }}>Cargando...</div>;
  if (!fac)   return null;

  const cfg   = fac._config || {};
  const items = fac.items   || [];
  const pagos = fac.pagos   || [];
  // QR y CUFE solo se muestran cuando la DIAN ACEPTÓ la factura
  // (estado_fe='aceptada' en producción, o dian_status_code='00' del set async).
  const aceptadaDian = !!fac.cufe && (fac.estado_fe === 'aceptada' || ['0','00'].includes(String(fac.dian_status_code || '')));
  const rechazadaDian = fac.estado_fe === 'rechazada';
  const cufeUrl = aceptadaDian ? `${DIAN_QR_BASE}${fac.cufe}` : null;

  return (
    <div>
      <button style={s.backBtn} onClick={() => navigate('/facturacion')}>← Facturación</button>

      {/* ── Encabezado ── */}
      <div style={s.header}>
        {/* Fila superior: info + estado/pago */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', width:'100%', marginBottom:14 }}>
          <div>
            <div style={s.topRow}>
              <span style={s.numero}>{fac.numero}</span>
              <span style={{ ...s.badge, background:EC[fac.estado]+'22', color:EC[fac.estado] }}>{fac.estado}</span>
              {aceptadaDian && <span style={{ ...s.badge, background:'#10b98122', color:'#10b981' }}>✓ Aceptada DIAN</span>}
              {rechazadaDian && <span style={s.badgeWarn}>✕ Rechazada por la DIAN</span>}
            </div>
            {(fac.tipo_documento_fe === '91' || fac.tipo_documento_fe === '92') && fac.ref_numero && (
              <p style={{ ...s.sub, marginTop:4, color:'#93c5fd', fontWeight:600 }}>
                {fac.tipo_documento_fe === '91' ? 'Nota crédito' : 'Nota débito'} de la factura {fac.ref_numero}
                {fac.ref_fecha ? ` · ${new Date(fac.ref_fecha).toLocaleDateString('es-CO')}` : ''}
              </p>
            )}
            <h1 style={s.h1}>{fac.cliente_nombre}</h1>
            <p style={s.sub}>NIT: {fac.nit} · {fac.tipo_documento || 'NIT'} · {fac.cliente_regimen || 'Régimen no especificado'}</p>
            <p style={{ ...s.sub, marginTop:2 }}>{fac.cliente_direccion || ''} {fac.cliente_ciudad ? `· ${fac.cliente_ciudad}` : ''}</p>
            {fac.cotizacion_numero && fac.tipo_documento_fe !== '91' && fac.tipo_documento_fe !== '92' && (
              <p style={{ ...s.sub, marginTop:2, color:'#a78bfa' }}>Desde la cotización {fac.cotizacion_numero}</p>
            )}
          </div>
        </div>

        {/* Barra de íconos — una sola línea, ancho completo */}
        <div style={s.iconBar}>
          {fac.estado === 'borrador' && (
            <IcoBtn title={enviandoDian ? 'Enviando…' : 'Enviar a DIAN'}
              onClick={handleEnviarDIAN}
              disabled={enviandoDian}
              svg={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M22 2 11 13"/><path d="M22 2 15 22 11 13 2 9l20-7z"/></svg>}
            />
          )}
          <IcoBtn title="PDF"
            onClick={() => descargarArchivo(`${API_BASE}/api/facturacion/${id}/pdf`, `${fac.numero}.pdf`)}
            svg={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8" rx="1"/></svg>}
          />
          <IcoBtn title="Correo"
            onClick={() => setEmail(true)}
            svg={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m2 7 10 7 10-7"/></svg>}
          />
          {fac.estado === 'aprobada' && (
            <IcoBtn title="Registrar pago"
              onClick={() => {
                setFormPago({ monto: fac.saldo, fecha_pago: hoyColombia(), medio_pago:'transferencia', referencia:'', notas:'' });
                setModalPago(true);
              }}
              svg={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>}
            />
          )}
          <IcoBtn title="ZIP DIAN"
            onClick={() => descargarArchivo(`${API_BASE}/api/facturacion/${id}/zip`, `${fac.numero}.zip`)}
            svg={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>}
          />
          {fac.tipo_documento_fe === '01' && fac.estado !== 'anulada' && <>
            <IcoBtn title="Nota débito"
              onClick={() => { setFormNota({ concepto_nota:'', tipo_nota:'1', notas:'', items:[ITEM0()], periodo_inicio:'', periodo_fin:'' }); setModalNota('debito'); }}
              svg={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>}
            />
            <IcoBtn title="Nota crédito"
              onClick={() => { setFormNota({ concepto_nota:'', tipo_nota:'1', notas:'', items:[ITEM0()], periodo_inicio:'', periodo_fin:'' }); setModalNota('credito'); }}
              svg={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>}
            />
          </>}
          <IcoBtn title="Adjuntar soporte"
            onClick={() => soportesRef.current?.abrir()}
            svg={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>}
          />
        </div>
      </div>

      {/* ── CUFE + QR: solo cuando la DIAN aceptó la factura ── */}
      {aceptadaDian && (
        <div style={{ display:'flex', gap:16, alignItems:'center', background:'#0f172a', border:'1px solid #1e40af', borderRadius:10, padding:'14px 16px', marginBottom:16 }}>
          {qrImg && <img src={qrImg} alt="QR DIAN" style={{ width:96, height:96, background:'#fff', borderRadius:6, padding:4, flexShrink:0 }} />}
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ color:'#10b981', fontSize:13, fontWeight:700, marginBottom:6 }}>✓ Factura electrónica validada por la DIAN</div>
            <div style={{ color:'#94a3b8', fontSize:10.5, textTransform:'uppercase', letterSpacing:.5, marginBottom:2 }}>CUFE</div>
            <div style={{ fontFamily:'monospace', fontSize:11, color:'#e2e8f0', wordBreak:'break-all', lineHeight:1.55 }}>{fac.cufe}</div>
            {cufeUrl && <a href={cufeUrl} target="_blank" rel="noreferrer" style={{ color:'#38bdf8', fontSize:12, textDecoration:'none', display:'inline-block', marginTop:8 }}>🔗 Verificar en el portal DIAN</a>}
          </div>
        </div>
      )}

      {/* ── PDF embebido ── */}
      <div style={s.pdfWrap}>
        {pdfLoading && (
          <div style={s.pdfLoading}>
            <div style={s.pdfSpinner} />
            <p style={{ color:'#64748b', margin:'12px 0 0', fontSize:13 }}>Generando PDF…</p>
          </div>
        )}
        {!pdfLoading && pdfUrl && (
          <iframe src={`${pdfUrl}#toolbar=0&navpanes=0&view=FitH`} style={s.pdfFrame} title={`Factura ${fac.numero}`} />
        )}
        {!pdfLoading && !pdfUrl && (
          <div style={s.pdfLoading}>
            <p style={{ color:'#64748b', fontSize:13 }}>No se pudo cargar el PDF</p>
          </div>
        )}
      </div>

      {/* ── Eventos bajo el PDF ── */}
      <div style={s.eventosWrap}>

        {/* RADIAN */}
        {fac.tipo_documento_fe === '01' && (
          <div style={s.eventoBloque}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <h2 style={s.sectionTitle}>🔗 Eventos RADIAN</h2>
              <span style={{ color:'#475569', fontSize:11 }}>DIAN Res. 000013/2021</span>
            </div>
            <div style={s.radianGuide}>
              {Object.entries(EVENTOS_RADIAN).map(([cod, meta]) => {
                const ev      = eventos.find(e => e.evento_codigo === cod);
                const abierto = radianAbierto === cod;
                return (
                  <div key={cod}
                    style={{ ...s.radianGuideItem, opacity: ev?1:0.4, borderLeft:`3px solid ${ev?meta.badge:'#334155'}`,
                      cursor: ev?'pointer':'default', background: abierto?'#162032':'#1e293b', flexDirection:'column', gap:0 }}
                    onClick={() => ev && setRadianAbierto(abierto ? null : cod)}
                  >
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ fontSize:16 }}>{meta.icon}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ color: ev?meta.badge:'#64748b', fontWeight:700, fontSize:11, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{cod} — {meta.label}</div>
                        <div style={{ color: ev?'#6ee7b7':'#475569', fontSize:10 }}>{ev ? `✓ ${ev.fecha}` : 'Pendiente'}</div>
                      </div>
                      {ev && <span style={{ color:'#475569', fontSize:10 }}>{abierto?'▲':'▼'}</span>}
                    </div>
                    {ev && abierto && (
                      <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid #334155' }} onClick={e=>e.stopPropagation()}>
                        <div style={{ color:'#94a3b8', fontSize:11, lineHeight:1.7 }}>
                          <div>📅 <strong style={{ color:'#f1f5f9' }}>{ev.fecha}</strong> &nbsp;⏱ {ev.hora}</div>
                          {ev.motivo_desc && <div>⚠️ {ev.motivo_desc}</div>}
                          {ev.responsable_nombre && <div>👤 {ev.responsable_nombre} {ev.responsable_apellido}{ev.responsable_cargo?` · ${ev.responsable_cargo}`:''}</div>}
                          {ev.cude && <div style={{ marginTop:6, fontFamily:'monospace', fontSize:10, color:'#475569', wordBreak:'break-all' }}>CUDE: {ev.cude.slice(0,40)}…</div>}
                        </div>
                        <div style={{ display:'flex', gap:6, marginTop:8 }}>
                          <button style={s.btnRadianXml} onClick={()=>descargarEventoXML(ev.id,ev.evento_codigo)}>📄 XML</button>
                          <button style={s.btnRadianDel} onClick={()=>eliminarEvento(ev.id)}>✕ Eliminar</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Notas asociadas */}
        {notas.length > 0 && (
          <div style={s.eventoBloque}>
            <h2 style={{ ...s.sectionTitle, marginBottom:10 }}>📝 Notas débito / crédito ({notas.length})</h2>
            <div style={s.card}>
              <table style={s.table}>
                <thead><tr>{['Número','Tipo','Concepto','Total','Estado'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {notas.map(n => (
                    <tr key={n.id} style={{ cursor:'pointer' }} onClick={()=>navigate(`/facturacion/${n.id}`)}>
                      <td style={{ ...s.td, color:'#93c5fd', fontWeight:600 }}>{n.numero}</td>
                      <td style={s.td}>{n.tipo_documento_fe==='92'?'Nota Débito':n.tipo_documento_fe==='91'?'Nota Crédito':'Doc. Equiv.'}</td>
                      <td style={s.td}>{n.concepto_nota||'—'}</td>
                      <td style={{ ...s.td, color:'#10b981', fontWeight:600 }}>{fmt(n.total)}</td>
                      <td style={s.td}><span style={{ ...s.badge, background:EC[n.estado]+'22', color:EC[n.estado] }}>{n.estado}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagos */}
        {pagos.length > 0 && (
          <div style={s.eventoBloque}>
            <h2 style={{ ...s.sectionTitle, marginBottom:10 }}>💳 Pagos registrados</h2>
            <div style={s.card}>
              <table style={s.table}>
                <thead><tr>{['Fecha','Monto','Medio','Referencia'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {pagos.map(p=>(
                    <tr key={p.id}>
                      <td style={s.td}>{fmtF(p.fecha_pago)}</td>
                      <td style={{ ...s.td, color:'#10b981', fontWeight:600 }}>{fmt(p.monto)}</td>
                      <td style={s.td}>{p.medio_pago}</td>
                      <td style={s.td}>{p.referencia||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Soportes / adjuntos */}
        <div id="soportes-fac" style={s.eventoBloque}>
          <SoportesFactura ref={soportesRef} facturaId={id} subir={false} />
        </div>

        {/* Historial */}
        <div style={s.eventoBloque}>
          <h2 style={{ ...s.sectionTitle, marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>
            Historial de actividad</h2>
          {historial.length === 0 ? (
            <p style={{ color:'#475569', fontSize:13 }}>Sin actividad registrada aún.</p>
          ) : (
            <div style={{ display:'flex', flexDirection:'column' }}>
              {historial.map((h, idx) => {
                const COLORES = { creacion:'#3b82f6', email:'#8b5cf6', pago:'#10b981', estado:'#f59e0b', nota:'#f97316', radian:'#818cf8', dian:'#06b6d4' };
                const color   = COLORES[h.tipo] || '#64748b';
                const fecha   = new Date(h.created_at);
                return (
                  <div key={h.id} style={{ display:'flex', gap:9, alignItems:'flex-start', paddingBottom:8, position:'relative' }}>
                    {idx < historial.length-1 && <div style={{ position:'absolute', left:11, top:24, bottom:0, width:2, background:'#1e293b', zIndex:0 }} />}
                    <div style={{ width:24, height:24, borderRadius:'50%', background:color+'22', border:`1.5px solid ${color}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, zIndex:1, color }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12" strokeLinecap="round" strokeLinejoin="round">{HIST_ICONS[h.tipo] || <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none"/>}</svg>
                    </div>
                    <div style={{ flex:1, paddingTop:2 }}>
                      <p style={{ margin:'0 0 1px', color:'#e2e8f0', fontSize:12, lineHeight:1.35 }}>{h.descripcion}</p>
                      <p style={{ margin:0, color:'#475569', fontSize:10 }}>
                        {fecha.toLocaleDateString('es-CO',{day:'2-digit',month:'short',year:'numeric'})} · {fecha.toLocaleTimeString('es-CO',{hour:'2-digit',minute:'2-digit'})}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* ── Modal email ── */}
      {email && <ModalEmail tipo="factura" id={id} clienteId={fac.cliente_id} destinatario={fac.cliente_email || ''} onClose={() => setEmail(false)} />}

      {/* ── Modal nota débito / crédito ── */}
      {modalNota && (() => {
        const esNC = modalNota === 'credito';
        const tipos = esNC ? TIPOS_NC : TIPOS_ND;
        const tipoSel = tipos.find(t => t.val === formNota.tipo_nota) || tipos[0];
        const esAnulacion = esNC && formNota.tipo_nota === '2';
        const necesitaPeriodo = !esNC && ['1','2'].includes(formNota.tipo_nota);
        const { sub, iv, total: tot } = calcNotaTotales();
        return (
          <div style={s.overlay}>
            <div style={{ ...s.modal, maxWidth:820, maxHeight:'93vh' }}>
              <div style={s.modalHeader}>
                <div>
                  <h2 style={{ ...s.modalTitle, marginBottom:2 }}>
                    {esNC ? '📉 Nueva Nota Crédito' : '📈 Nueva Nota Débito'}
                    <span style={{ fontSize:12, fontWeight:400, color:'#64748b', marginLeft:10 }}>
                      {esNC ? 'Tipodoc. DIAN 91' : 'Tipodoc. DIAN 92'}
                    </span>
                  </h2>
                  <p style={{ color:'#475569', fontSize:12, margin:0 }}>Factura referenciada: <strong style={{ color:'#93c5fd' }}>{fac.numero}</strong></p>
                </div>
                <button style={s.closeBtn} onClick={() => setModalNota(null)}>✕</button>
              </div>
              <div style={{ ...s.modalBody, overflowY:'auto' }}>
                {/* Info factura original */}
                <div style={{ background:'#0f172a', borderRadius:8, padding:'10px 16px', marginBottom:14, fontSize:12, color:'#64748b', display:'flex', gap:16, flexWrap:'wrap', border:'1px solid #1e293b' }}>
                  <span>👤 <strong style={{ color:'#f1f5f9' }}>{fac.cliente_nombre}</strong></span>
                  <span>NIT: <strong style={{ color:'#f1f5f9' }}>{fac.nit}</strong></span>
                  <span>Total original: <strong style={{ color:'#10b981' }}>{fmt(fac.total)}</strong></span>
                  {fac.cufe && <span style={{ fontFamily:'monospace', fontSize:10 }}>CUFE: {fac.cufe.slice(0,20)}…</span>}
                </div>

                {/* Tipo de nota */}
                <div style={s.row2}>
                  <div style={s.field}>
                    <label style={s.label}>{esNC ? 'Concepto de corrección (DIAN) *' : 'Tipo de nota débito (DIAN) *'}</label>
                    <select style={s.input} value={formNota.tipo_nota}
                      onChange={e => setFormNota(f => ({ ...f, tipo_nota: e.target.value }))}>
                      {tipos.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                    </select>
                    {tipoSel.desc && (
                      <p style={{ color:'#475569', fontSize:11, margin:'5px 0 0', lineHeight:1.4 }}>ℹ️ {tipoSel.desc}</p>
                    )}
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Concepto / descripción detallada *</label>
                    <input style={s.input} value={formNota.concepto_nota}
                      onChange={e => setFormNota(f => ({ ...f, concepto_nota: e.target.value }))}
                      placeholder={esNC ? 'Ej: Devolución de 5 unidades por daño en tránsito' : 'Ej: Intereses de mora — Factura vencida 30 días'} />
                  </div>
                </div>

                {/* Período (para ND tipos 1-2: intereses / gastos) */}
                {necesitaPeriodo && (
                  <div style={{ ...s.row2, marginTop:-4, marginBottom:4 }}>
                    <div style={s.field}>
                      <label style={s.label}>Período — fecha inicio</label>
                      <input style={s.input} type="date" value={formNota.periodo_inicio}
                        onChange={e => setFormNota(f => ({ ...f, periodo_inicio: e.target.value }))} />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>Período — fecha fin</label>
                      <input style={s.input} type="date" value={formNota.periodo_fin}
                        onChange={e => setFormNota(f => ({ ...f, periodo_fin: e.target.value }))} />
                    </div>
                  </div>
                )}

                {/* Aviso anulación total */}
                {esAnulacion && (
                  <div style={{ background:'#450a0a', border:'1px solid #dc2626', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:'#fca5a5' }}>
                    ⚠️ <strong>Anulación total (código 2):</strong> La factura <strong>{fac.numero}</strong> quedará en estado <strong>anulada</strong> automáticamente. Los ítems deben coincidir con los de la factura original.
                  </div>
                )}

                {/* Ítems */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'8px 0 6px' }}>
                  <span style={{ color:'#f1f5f9', fontWeight:600, fontSize:13 }}>
                    Ítems de la {esNC ? 'nota crédito' : 'nota débito'}
                  </span>
                  <div style={{ display:'flex', gap:8 }}>
                    <button style={{ ...s.btnSmAdd, background:'#1e3a5f', color:'#93c5fd' }}
                      onClick={() => cargarItemsOriginal(esAnulacion)}>
                      📋 {esAnulacion ? 'Cargar todos los ítems originales' : 'Cargar ítems originales'}
                    </button>
                    <button style={s.btnSmAdd} onClick={addItemNota}>+ Ítem</button>
                  </div>
                </div>
                {/* Cabecera ítems */}
                <div style={{ display:'flex', gap:8, marginBottom:4, padding:'0 4px' }}>
                  {[['Descripción',3],['Cant.',1],['Und.',0.9],['Precio',1.4],['Dto%',0.7],['IVA%',0.7],['Total',1.1],['',0.4]].map(([h,f]) => (
                    <div key={h} style={{ flex:f, color:'#475569', fontSize:10, textTransform:'uppercase', letterSpacing:.5 }}>{h}</div>
                  ))}
                </div>
                {formNota.items.map((it, i) => {
                  const base = parseFloat(it.cantidad||0)*parseFloat(it.precio_unitario||0)*(1-(parseFloat(it.descuento_pct||0)/100));
                  const totIt = base*(1+(parseFloat(it.iva_pct??19)/100));
                  return (
                    <div key={i} style={s.itemRow}>
                      <input style={{ ...s.input, flex:3 }} placeholder="Descripción" value={it.descripcion} onChange={e=>updItemNota(i,'descripcion',e.target.value)} />
                      <input style={{ ...s.input, flex:1 }} type="number" value={it.cantidad} onChange={e=>updItemNota(i,'cantidad',e.target.value)} />
                      <input style={{ ...s.input, flex:0.9 }} value={it.unidad} onChange={e=>updItemNota(i,'unidad',e.target.value)} />
                      <input style={{ ...s.input, flex:1.4 }} type="number" placeholder="Precio" value={it.precio_unitario} onChange={e=>updItemNota(i,'precio_unitario',e.target.value)} />
                      <input style={{ ...s.input, flex:0.7 }} type="number" min="0" max="100" value={it.descuento_pct} onChange={e=>updItemNota(i,'descuento_pct',e.target.value)} />
                      <input style={{ ...s.input, flex:0.7 }} type="number" min="0" max="100" value={it.iva_pct} onChange={e=>updItemNota(i,'iva_pct',e.target.value)} />
                      <div style={{ flex:1.1, color:'#10b981', fontSize:12, fontWeight:600 }}>{fmt(totIt)}</div>
                      {formNota.items.length > 1 && <button style={{ ...s.removeBtn, flex:0.4 }} onClick={()=>removeItemNota(i)}>✕</button>}
                    </div>
                  );
                })}

                {/* Totales */}
                <div style={{ ...s.totalesBox, marginTop:10 }}>
                  <div style={s.totalRow}><span>Subtotal</span><span>{fmt(sub)}</span></div>
                  <div style={s.totalRow}><span>IVA</span><span>{fmt(iv)}</span></div>
                  <div style={{ ...s.totalRow, ...s.totalFinal }}>
                    <span>Total {esNC ? 'nota crédito' : 'nota débito'}</span>
                    <span>{fmt(tot)}</span>
                  </div>
                </div>

                {/* Observaciones */}
                <div style={{ ...s.field, marginTop:12 }}>
                  <label style={s.label}>Observaciones adicionales</label>
                  <textarea style={{ ...s.input, height:56, padding:'8px 12px', resize:'vertical' }}
                    placeholder="Información adicional para el cliente o contabilidad..."
                    value={formNota.notas} onChange={e => setFormNota(f => ({ ...f, notas: e.target.value }))} />
                </div>
              </div>
              <div style={s.modalFooter}>
                <button style={s.btnSecondary} onClick={() => setModalNota(null)}>Cancelar</button>
                <button style={{ ...s.btnPrimary, opacity:saving?0.7:1 }} onClick={handleCrearNota} disabled={saving}>
                  {saving ? 'Generando CUDE...' : `⚡ Crear ${esNC ? 'Nota Crédito' : 'Nota Débito'}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal Documento Equivalente ── */}
      {modalDE && (() => {
        const { sub, iv, total: tot } = calcDETotales();
        return (
          <div style={s.overlay}>
            <div style={{ ...s.modal, maxWidth:820, maxHeight:'93vh' }}>
              <div style={s.modalHeader}>
                <div>
                  <h2 style={{ ...s.modalTitle, marginBottom:2 }}>🧾 Nuevo Documento Equivalente</h2>
                  <p style={{ color:'#475569', fontSize:12, margin:0 }}>Art. 616-1 ET — No requiere CUFE/CUDE ni envío a la DIAN</p>
                </div>
                <button style={s.closeBtn} onClick={() => setModalDE(false)}>✕</button>
              </div>
              <div style={{ ...s.modalBody, overflowY:'auto' }}>
                <div style={{ background:'#0f172a', borderRadius:8, padding:'10px 16px', marginBottom:14, fontSize:12, color:'#64748b', display:'flex', gap:16, flexWrap:'wrap', border:'1px solid #1e293b' }}>
                  <span>👤 <strong style={{ color:'#f1f5f9' }}>{fac.cliente_nombre}</strong></span>
                  <span>NIT: <strong style={{ color:'#f1f5f9' }}>{fac.nit}</strong></span>
                </div>

                <div style={s.row2}>
                  <div style={s.field}>
                    <label style={s.label}>Tipo de documento equivalente *</label>
                    <select style={s.input} value={formDE.tipo_de}
                      onChange={e => setFormDE(f => ({ ...f, tipo_de: e.target.value }))}>
                      {TIPOS_DE.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                    </select>
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Concepto *</label>
                    <input style={s.input} value={formDE.concepto}
                      onChange={e => setFormDE(f => ({ ...f, concepto: e.target.value }))}
                      placeholder="Ej: Venta mostrador — efectivo" />
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Medio de pago</label>
                    <select style={s.input} value={formDE.medio_pago_desc}
                      onChange={e => setFormDE(f => ({ ...f, medio_pago_desc: e.target.value }))}>
                      {['Efectivo','Transferencia bancaria','Tarjeta débito','Tarjeta crédito','Cheque','Otro'].map(m =>
                        <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Observaciones</label>
                    <input style={s.input} value={formDE.notas}
                      onChange={e => setFormDE(f => ({ ...f, notas: e.target.value }))}
                      placeholder="Opcional" />
                  </div>
                </div>

                {/* Ítems */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'8px 0 6px' }}>
                  <span style={{ color:'#f1f5f9', fontWeight:600, fontSize:13 }}>Ítems</span>
                  <button style={s.btnSmAdd} onClick={addItemDE}>+ Ítem</button>
                </div>
                <div style={{ display:'flex', gap:8, marginBottom:4, padding:'0 4px' }}>
                  {[['Descripción',3],['Cant.',1],['Und.',0.9],['Precio',1.4],['Dto%',0.7],['IVA%',0.7],['Total',1.1],['',0.4]].map(([h,f]) => (
                    <div key={h} style={{ flex:f, color:'#475569', fontSize:10, textTransform:'uppercase', letterSpacing:.5 }}>{h}</div>
                  ))}
                </div>
                {formDE.items.map((it, i) => {
                  const base = parseFloat(it.cantidad||0)*parseFloat(it.precio_unitario||0)*(1-(parseFloat(it.descuento_pct||0)/100));
                  const totIt = base*(1+(parseFloat(it.iva_pct??0)/100));
                  return (
                    <div key={i} style={s.itemRow}>
                      <input style={{ ...s.input, flex:3 }} placeholder="Descripción" value={it.descripcion} onChange={e=>updItemDE(i,'descripcion',e.target.value)} />
                      <input style={{ ...s.input, flex:1 }} type="number" value={it.cantidad} onChange={e=>updItemDE(i,'cantidad',e.target.value)} />
                      <input style={{ ...s.input, flex:0.9 }} value={it.unidad} onChange={e=>updItemDE(i,'unidad',e.target.value)} />
                      <input style={{ ...s.input, flex:1.4 }} type="number" placeholder="Precio" value={it.precio_unitario} onChange={e=>updItemDE(i,'precio_unitario',e.target.value)} />
                      <input style={{ ...s.input, flex:0.7 }} type="number" min="0" max="100" value={it.descuento_pct} onChange={e=>updItemDE(i,'descuento_pct',e.target.value)} />
                      <input style={{ ...s.input, flex:0.7 }} type="number" min="0" max="100" value={it.iva_pct} onChange={e=>updItemDE(i,'iva_pct',e.target.value)} />
                      <div style={{ flex:1.1, color:'#10b981', fontSize:12, fontWeight:600 }}>{fmt(totIt)}</div>
                      {formDE.items.length > 1 && <button style={{ ...s.removeBtn, flex:0.4 }} onClick={()=>removeItemDE(i)}>✕</button>}
                    </div>
                  );
                })}

                <div style={{ ...s.totalesBox, marginTop:10 }}>
                  <div style={s.totalRow}><span>Subtotal</span><span>{fmt(sub)}</span></div>
                  <div style={s.totalRow}><span>IVA</span><span>{fmt(iv)}</span></div>
                  <div style={{ ...s.totalRow, ...s.totalFinal }}><span>Total documento</span><span>{fmt(tot)}</span></div>
                </div>

                <div style={{ background:'#162032', border:'1px solid #1e3a5f', borderRadius:8, padding:'10px 14px', marginTop:12, fontSize:11, color:'#64748b', lineHeight:1.6 }}>
                  📌 El <strong style={{ color:'#93c5fd' }}>Documento Equivalente</strong> se usa para operaciones donde no se emite factura electrónica (ventas al detal en POS, tiquetes, etc.). Se genera un número interno y queda registrado para efectos contables. No requiere envío a la DIAN ni firma electrónica.
                </div>
              </div>
              <div style={s.modalFooter}>
                <button style={s.btnSecondary} onClick={() => setModalDE(false)}>Cancelar</button>
                <button style={{ ...s.btnPrimary, background:'#065f46', opacity:saving?0.7:1 }} onClick={handleCrearDE} disabled={saving}>
                  {saving ? 'Guardando...' : '🧾 Crear Documento Equivalente'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal RADIAN ── */}
      {modalRadian && (
        <div style={s.overlay}>
          <div style={{ ...s.modal, maxWidth:520 }}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>🔗 Registrar Evento RADIAN</h2>
              <button style={s.closeBtn} onClick={() => setModalRadian(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              {/* Info de la factura */}
              <div style={{ background:'#162032', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'#94a3b8' }}>
                Factura: <strong style={{ color:'#93c5fd' }}>{fac.numero}</strong> · Cliente: <strong style={{ color:'#f1f5f9' }}>{fac.cliente_nombre}</strong>
              </div>

              {/* Código de evento */}
              <div style={s.field}>
                <label style={s.label}>Tipo de evento *</label>
                <select style={s.input} value={formRadian.codigo}
                  onChange={e => setFormRadian(f => ({ ...f, codigo: e.target.value }))}>
                  {Object.entries(EVENTOS_RADIAN).map(([cod, meta]) => (
                    <option key={cod} value={cod}>{cod} — {meta.label}</option>
                  ))}
                </select>
                <p style={{ color:'#64748b', fontSize:11, margin:'6px 0 0', lineHeight:1.4 }}>
                  {EVENTOS_RADIAN[formRadian.codigo]?.desc}
                </p>
              </div>

              {/* Flujo de eventos — advertencia */}
              {['031','032','033'].includes(formRadian.codigo) && !eventos.find(e => e.evento_codigo === '030') && (
                <div style={{ background:'#450a0a', border:'1px solid #dc2626', borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:12, color:'#fca5a5' }}>
                  ⚠️ El evento <strong>030 (Acuse de recibo)</strong> debe registrarse antes que este evento.
                </div>
              )}
              {formRadian.codigo === '034' && eventos.find(e => e.evento_codigo === '033') && (
                <div style={{ background:'#450a0a', border:'1px solid #dc2626', borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:12, color:'#fca5a5' }}>
                  ⚠️ No puede registrar Aceptación Tácita porque ya existe una Aceptación Expresa (033).
                </div>
              )}

              {/* Campos adicionales para evento 031 — Reclamo */}
              {formRadian.codigo === '031' && (
                <>
                  <div style={s.field}>
                    <label style={s.label}>Motivo del reclamo *</label>
                    <select style={s.input} value={formRadian.motivo_codigo}
                      onChange={e => setFormRadian(f => ({ ...f, motivo_codigo: e.target.value }))}>
                      {Object.entries(MOTIVOS_031).map(([k, v]) => (
                        <option key={k} value={k}>{k} — {v}</option>
                      ))}
                    </select>
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Descripción del reclamo</label>
                    <input style={s.input} placeholder="Detalle del reclamo..." value={formRadian.motivo_desc}
                      onChange={e => setFormRadian(f => ({ ...f, motivo_desc: e.target.value }))} />
                  </div>
                </>
              )}

              {/* Responsable — para eventos 030 y 032 */}
              {(formRadian.codigo === '030' || formRadian.codigo === '032') && (
                <>
                  <p style={{ color:'#94a3b8', fontSize:12, fontWeight:600, margin:'8px 0 12px', textTransform:'uppercase', letterSpacing:.6 }}>
                    Datos del responsable (persona que registra el evento)
                  </p>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                    <div style={s.field}>
                      <label style={s.label}>Cédula / ID</label>
                      <input style={s.input} placeholder="12345678" value={formRadian.responsable.id}
                        onChange={e => setFormRadian(f => ({ ...f, responsable:{ ...f.responsable, id:e.target.value } }))} />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>Nombre</label>
                      <input style={s.input} value={formRadian.responsable.nombre}
                        onChange={e => setFormRadian(f => ({ ...f, responsable:{ ...f.responsable, nombre:e.target.value } }))} />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>Apellido</label>
                      <input style={s.input} value={formRadian.responsable.apellido}
                        onChange={e => setFormRadian(f => ({ ...f, responsable:{ ...f.responsable, apellido:e.target.value } }))} />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>Cargo</label>
                      <input style={s.input} placeholder="Ej: Gerente de Compras" value={formRadian.responsable.cargo}
                        onChange={e => setFormRadian(f => ({ ...f, responsable:{ ...f.responsable, cargo:e.target.value } }))} />
                    </div>
                    <div style={{ ...s.field, gridColumn:'1/-1' }}>
                      <label style={s.label}>Área / Departamento</label>
                      <input style={s.input} placeholder="Ej: Compras" value={formRadian.responsable.area}
                        onChange={e => setFormRadian(f => ({ ...f, responsable:{ ...f.responsable, area:e.target.value } }))} />
                    </div>
                  </div>
                </>
              )}

              {/* Info 034 */}
              {formRadian.codigo === '034' && (
                <div style={{ background:'#1e1b4b', border:'1px solid #818cf8', borderRadius:8, padding:'10px 14px', fontSize:12, color:'#a5b4fc', lineHeight:1.6 }}>
                  <strong>Aceptación Tácita (034):</strong> Este evento se genera automáticamente cuando han transcurrido 3 días hábiles desde la recepción de la factura sin que el adquiriente haya aceptado o rechazado expresamente. El ReceiverParty en el XML es la DIAN (NIT 800197268).
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => setModalRadian(false)}>Cancelar</button>
              <button style={{ ...s.btnPrimary, opacity: savingRadian ? 0.7 : 1 }}
                onClick={handleCrearEventoRadian} disabled={savingRadian}>
                {savingRadian ? 'Generando XML...' : `⚡ Registrar Evento ${formRadian.codigo}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal pago ── */}
      {modalPago && (
        <div style={s.overlay}>
          <div style={{ ...s.modal, maxWidth:480 }}>
            <div style={s.modalHeader}><h2 style={s.modalTitle}>Registrar pago</h2><button style={s.closeBtn} onClick={() => setModalPago(false)}>✕</button></div>
            <div style={s.modalBody}>
              <div style={s.row2}>
                <div style={s.field}><label style={s.label}>Monto *</label>
                  <input style={s.input} type="number" value={formPago.monto} onChange={e => setFormPago({...formPago, monto:e.target.value})} /></div>
                <div style={s.field}><label style={s.label}>Fecha</label>
                  <input style={s.input} type="date" value={formPago.fecha_pago} onChange={e => setFormPago({...formPago, fecha_pago:e.target.value})} /></div>
                <div style={s.field}><label style={s.label}>Medio de pago</label>
                  <select style={s.input} value={formPago.medio_pago} onChange={e => setFormPago({...formPago, medio_pago:e.target.value})}>
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="cheque">Cheque</option>
                    <option value="tarjeta">Tarjeta</option>
                  </select></div>
                <div style={s.field}><label style={s.label}>Referencia</label>
                  <input style={s.input} value={formPago.referencia} onChange={e => setFormPago({...formPago, referencia:e.target.value})} /></div>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => setModalPago(false)}>Cancelar</button>
              <button style={{ ...s.btnPrimary, opacity:saving?0.7:1 }} onClick={handlePago} disabled={saving}>{saving ? 'Guardando...' : 'Registrar pago'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IcoBtn({ svg, title, onClick, disabled }) {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        flex:          1,
        background:    hover && !disabled ? '#334155' : 'transparent',
        border:        '1px solid #334155',
        borderRadius:  8,
        color:         '#e2e8f0',
        cursor:        disabled ? 'wait' : 'pointer',
        opacity:       disabled ? 0.55 : 1,
        display:       'flex',
        flexDirection: 'column',
        alignItems:    'center',
        justifyContent:'center',
        gap:           4,
        padding:       '8px 6px 6px',
        transition:    'background .15s',
        minWidth:      0,
      }}
    >
      <span style={{ width:18, height:18, display:'block' }}>{svg}</span>
      <span style={{ fontSize:9, color:'#94a3b8', whiteSpace:'nowrap', letterSpacing:.3, overflow:'hidden', textOverflow:'ellipsis', maxWidth:'100%' }}>{title}</span>
    </button>
  );
}

function Card({ label, value, sub, big, color }) {
  return (
    <div style={s.infoCard}>
      <p style={s.infoLabel}>{label}</p>
      <p style={{ ...s.infoVal, fontSize: big ? 18 : 15, color: color || '#f1f5f9' }}>{value}</p>
      {sub && <p style={{ color:'#64748b', fontSize:11, margin:0 }}>{sub}</p>}
    </div>
  );
}

const s = {
  backBtn:     { background:'transparent', border:'none', color:'#64748b', cursor:'pointer', fontSize:13, padding:'0 0 12px', display:'block' },
  header:      { display:'flex', flexDirection:'column', marginBottom:20 },
  topRow:      { display:'flex', alignItems:'center', gap:10, marginBottom:6, flexWrap:'wrap' },
  numero:      { color:'#3b82f6', fontSize:18, fontWeight:700, fontFamily:'monospace' },
  h1:          { color:'#f1f5f9', fontSize:22, fontWeight:700, margin:'0 0 4px' },
  sub:         { color:'#64748b', fontSize:13, margin:0 },
  badge:       { padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600 },
  badgeWarn:   { background:'#78350f33', color:'#f59e0b', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:600 },
  actionsRow:   { display:'flex', gap:6, alignItems:'center' },
  iconBar:      { display:'flex', gap:6, width:'100%' },
  estadoSelect:{ background:'#1e293b', border:'1px solid #334155', color:'#f1f5f9', borderRadius:8, padding:'8px 14px', fontSize:13 },
  btnSuccess:  { background:'#166534', color:'#86efac', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer' },
  btnSecondary:{ background:'#334155', color:'#cbd5e1', border:'none', borderRadius:8, padding:'10px 20px', fontSize:14, cursor:'pointer' },
  btnPrimary:  { background:'#1d4ed8', color:'#fff', border:'none', borderRadius:8, padding:'10px 22px', fontSize:14, fontWeight:600, cursor:'pointer' },
  pdfWrap:     { background:'#374151', borderRadius:12, border:'1px solid #334155', marginBottom:20, overflow:'auto', minHeight:200, display:'flex', flexDirection:'column', alignItems:'center', padding:'24px 16px' },
  pdfFrame:    { width:'794px', height:'1123px', border:'none', display:'block', boxShadow:'0 4px 24px rgba(0,0,0,0.5)', background:'#fff', flexShrink:0 },
  pdfLoading:  { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:200, padding:32 },
  pdfSpinner:  { width:36, height:36, border:'3px solid #1e293b', borderTop:'3px solid #3b82f6', borderRadius:'50%', animation:'spin 0.8s linear infinite' },
  eventosWrap: { display:'flex', flexDirection:'column', gap:20 },
  eventoBloque:{ background:'#0f172a', borderRadius:12, padding:'20px 24px', border:'1px solid #1e293b' },
  section:     { marginBottom:24 },
  sectionTitle:{ color:'#f1f5f9', fontSize:16, fontWeight:600, marginBottom:12 },
  card:        { background:'#1e293b', borderRadius:12, overflow:'auto' },
  table:       { width:'100%', borderCollapse:'collapse', minWidth:700 },
  th:          { padding:'11px 14px', color:'#64748b', fontSize:11, textAlign:'left', borderBottom:'1px solid #334155', background:'#162032', whiteSpace:'nowrap' },
  td:          { padding:'11px 14px', color:'#cbd5e1', fontSize:13, borderBottom:'1px solid #1a2332' },
  notasCard:   { background:'#1e293b', borderRadius:10, padding:'16px 20px', border:'1px solid #334155', marginBottom:20 },
  overlay:     { position:'fixed', inset:0, background:'rgba(0,0,0,.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100 },
  modal:       { background:'#1e293b', borderRadius:16, width:'90%', display:'flex', flexDirection:'column', border:'1px solid #334155' },
  modalHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'13px 20px', borderBottom:'1px solid #334155', flexShrink:0 },
  modalTitle:  { color:'#f1f5f9', fontSize:16, fontWeight:700, margin:0 },
  closeBtn:    { background:'transparent', border:'none', color:'#64748b', fontSize:18, cursor:'pointer' },
  modalBody:   { padding:'14px 20px', overflowY:'auto', flex:1 },
  modalFooter: { display:'flex', justifyContent:'flex-end', gap:10, padding:'12px 20px', borderTop:'1px solid #334155', flexShrink:0 },
  row2:        { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 },
  field:       { marginBottom:10 },
  label:       { display:'block', color:'#94a3b8', fontSize:12, marginBottom:4, fontWeight:500 },
  input:       { width:'100%', height:34, padding:'0 12px', background:'#0f172a', border:'1px solid #334155', borderRadius:8, color:'#f1f5f9', fontSize:13, boxSizing:'border-box', outline:'none' },
  btnXML:      { flex:1, background:'#064e3b', color:'#6ee7b7', border:'none', borderRadius:6, padding:'8px 12px', fontSize:12, fontWeight:600, cursor:'pointer', textAlign:'center' },
  btnZIP:      { flex:1, background:'#1e3a5f', color:'#93c5fd', border:'none', borderRadius:6, padding:'8px 12px', fontSize:12, fontWeight:600, cursor:'pointer', textAlign:'center' },
  btnNota:     { flex:1, background:'#78350f', color:'#fed7aa', border:'none', borderRadius:6, padding:'8px 12px', fontSize:12, fontWeight:600, cursor:'pointer', textAlign:'center' },
  btnNotaNC:   { flex:1, background:'#1e3a5f', color:'#93c5fd', border:'none', borderRadius:6, padding:'8px 12px', fontSize:12, fontWeight:600, cursor:'pointer', textAlign:'center' },
  btnDE:       { flex:1, background:'#064e3b', color:'#6ee7b7', border:'none', borderRadius:6, padding:'8px 12px', fontSize:12, fontWeight:600, cursor:'pointer', textAlign:'center' },
  btnSmAdd:    { background:'#1e40af', color:'#93c5fd', border:'none', borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer' },
  removeBtn:   { background:'#450a0a', border:'none', color:'#fca5a5', borderRadius:6, padding:'4px 8px', cursor:'pointer', fontSize:12 },
  itemRow:     { display:'flex', gap:8, alignItems:'center', marginBottom:8 },
  totalesBox:  { background:'#0f172a', borderRadius:10, padding:'14px 18px' },
  totalRow:    { display:'flex', justifyContent:'space-between', color:'#94a3b8', fontSize:13, marginBottom:6 },
  totalFinal:  { color:'#f1f5f9', fontWeight:700, fontSize:15, borderTop:'1px solid #334155', paddingTop:8, marginTop:4 },
  btnSecondary: { background:'#334155', color:'#cbd5e1', border:'none', borderRadius:8, padding:'10px 20px', fontSize:14, cursor:'pointer' },
  btnPrimary:   { background:'#1d4ed8', color:'#fff', border:'none', borderRadius:8, padding:'10px 22px', fontSize:14, fontWeight:600, cursor:'pointer' },
  // RADIAN styles
  btnRadian:    { background:'#312e81', color:'#a5b4fc', border:'1px solid #4338ca', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' },
  radianEmpty:  { background:'#1e293b', borderRadius:12, padding:'32px 24px', textAlign:'center', border:'1px dashed #334155' },
  radianBadge:  { padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700, marginRight:8, fontFamily:'monospace' },
  radianNombre: { color:'#f1f5f9', fontSize:13, fontWeight:600 },
  radianMeta:   { display:'flex', flexWrap:'wrap', gap:12, marginTop:8, color:'#64748b', fontSize:11 },
  radianCude:   { marginTop:8, paddingTop:8, borderTop:'1px solid #1a2332', wordBreak:'break-all' },
  btnRadianXml: { background:'#064e3b', color:'#6ee7b7', border:'none', borderRadius:6, padding:'4px 10px', fontSize:11, fontWeight:600, cursor:'pointer' },
  btnRadianDel: { background:'#450a0a', color:'#fca5a5', border:'none', borderRadius:6, padding:'4px 8px', fontSize:11, cursor:'pointer' },
  radianGuide:  { display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:8, marginTop:4 },
  radianGuideItem:{ background:'#1e293b', borderRadius:8, padding:'10px 12px', border:'1px solid #334155', display:'flex', gap:8, alignItems:'flex-start', transition:'background .15s', userSelect:'none' },
};
